import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { MAX_ORDER_HEAD_COUNT } from "@constants/order.constants";

// Create / edit settings. The office sets the address, date and caterer itself.
export const GroupOrderForm = ({ initial, onSubmit, onCancel }: any) => {
  const [form, setForm] = useState<any>({
    label: initial?.label || "",
    totalBudget: initial?.totalBudget ?? "",
    perPersonBudget: initial?.perPersonBudget || "",
    maxHeadCount: initial?.maxHeadCount ?? 10,
  });
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const set = (patch: any) => setForm((f: any) => ({ ...f, ...patch }));

  const total = Number(form.totalBudget);
  const perPerson = form.perPersonBudget === "" ? 0 : Number(form.perPersonBudget);
  const headCount = Number(form.maxHeadCount);
  const errors = {
    label: !form.label.trim()
      ? "Give it a name you'll recognize"
      : form.label.length > 120
        ? "120 characters max"
        : "",
    totalBudget: !(total > 0) ? "Enter a total budget above $0" : "",
    perPersonBudget: perPerson < 0 || !Number.isFinite(perPerson) ? "Can't be negative" : "",
    maxHeadCount: !(Number.isInteger(headCount) && headCount >= 1) ? "At least 1 person" : "",
  };
  const valid = !Object.values(errors).some(Boolean);

  const submit = async () => {
    setTouched(true);
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({
        label: form.label.trim(),
        totalBudget: total,
        perPersonBudget: perPerson,
        maxHeadCount: headCount,
      });
    } finally {
      setSubmitting(false);
    }
  };
  const err = (k: keyof typeof errors) => (touched ? errors[k] : "");

  return (
    <Dialog open onClose={submitting ? undefined : onCancel} fullWidth maxWidth="sm">
      <DialogTitle>{initial ? "Edit admin order" : "Start admin order"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          required
          margin="dense"
          label="Name (e.g. Dr. Patel, Tuesday)"
          value={form.label}
          onChange={(e) => set({ label: e.target.value })}
          error={!!err("label")}
          helperText={err("label") || " "}
          inputProps={{ maxLength: 120 }}
        />
        <TextField
          fullWidth
          required
          margin="dense"
          type="number"
          label="Total budget ($)"
          inputProps={{ min: 0, step: 0.01 }}
          value={form.totalBudget}
          onChange={(e) => set({ totalBudget: e.target.value })}
          error={!!err("totalBudget")}
          helperText={err("totalBudget") || "The full order total, including tax, fees and tip"}
        />
        <TextField
          fullWidth
          margin="dense"
          type="number"
          label="Budget per person ($, optional)"
          inputProps={{ min: 0, step: 0.01 }}
          value={form.perPersonBudget}
          onChange={(e) => set({ perPersonBudget: e.target.value })}
          error={!!err("perPersonBudget")}
          helperText={err("perPersonBudget") || " "}
        />
        <TextField
          fullWidth
          required
          margin="dense"
          type="number"
          label="Max headcount"
          inputProps={{ min: 1, max: MAX_ORDER_HEAD_COUNT }}
          value={form.maxHeadCount}
          onChange={(e) => set({ maxHeadCount: e.target.value })}
          error={!!err("maxHeadCount")}
          helperText={err("maxHeadCount") || "A guideline; the office can change it"}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" disabled={submitting} onClick={submit}>
          {initial ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
